import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AddressInput } from "@/components/AddressInput";
import { FareBreakdown } from "@/components/FareBreakdown";
import { MapView } from "@/components/MapView";
import { VehicleCard } from "@/components/VehicleCard";
import { RequestDriver } from "@/components/RequestDriver";
import { FoodRequest, emptyFood, foodPayload, foodBlocked } from "@/components/FoodRequest";
import { CLASS_LABEL } from "@/components/FareBreakdown";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOCATION_REQUIRED_MSG, useCustomerLocation } from "@/context/CustomerLocationContext";
import { LocationRequiredBanner } from "@/components/LocationRequired";
import { CardOnFile } from "@/components/CardOnFile";
import { AdSlot } from "@/components/AdSlot";
import { PaymentMethodPicker, afterBooking, payLabel } from "@/components/PaymentMethodPicker";

const empty = () => ({ text: "", point: null, error: "" });
const takeDropoff = () => {
  try {
    const d = JSON.parse(sessionStorage.getItem("lux_dropoff") || "null");
    sessionStorage.removeItem("lux_dropoff");
    return d && d.lat != null ? { text: d.address, point: d, error: "" } : empty();
  } catch { return empty(); }
};
const MAX_STOPS = 10;

export const PaymentGate = CardOnFile;

export const BookingPanel = () => {
  const { user } = useAuth();
  const [pickup, setPickup] = useState(empty());
  const [stops, setStops] = useState(() => [takeDropoff()]);
  const [method, setMethod] = useState("card_on_file");
  const [stopError, setStopError] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [booking, setBooking] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  const [vehicleClass, setVehicleClass] = useState("standard");
  const fare = quote?.options?.find((o) => o.vehicle_class === vehicleClass)?.fare || quote?.fare;

  useEffect(() => {
    const take = (code) => { if (code) { setPromoCode(code); setPromoError(""); sessionStorage.removeItem("lux_promo"); } };
    take(sessionStorage.getItem("lux_promo"));
    const onPromo = (e) => take(e.detail);
    window.addEventListener("lux-promo", onPromo);
    return () => window.removeEventListener("lux-promo", onPromo);
  }, []);

  const ready = pickup.point && stops.every((s) => s.point);
  const key = ready ? [pickup, ...stops].map((p) => `${p.point.lat},${p.point.lng}`).join("|") + `#${promoCode}` : "";

  useEffect(() => {
    if (!ready) { setQuote(null); return; }
    let alive = true;
    setQuoting(true);
    api.post("/trips/estimate", { pickup: pickup.point, stops: stops.map((s) => s.point), promo_code: promoCode || null })
      .then(({ data }) => {
        if (!alive) return;
        setQuote(data);
        if (data.promo_error) { setPromoError(data.promo_error); setPromoCode(""); }
      })
      .catch((e) => alive && toast.error(errMsg(e)))
      .finally(() => alive && setQuoting(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const applyPromo = async (e) => {
    e.preventDefault();
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoError("");
    try {
      await api.post("/promos/validate", { code });
      setPromoCode(code);
      setPromoInput("");
    } catch (err) {
      setPromoError(errMsg(err));
    }
  };

  const addStop = () => {
    if (stops.length >= MAX_STOPS) { setStopError("Maximum 10 stops"); return; }
    setStopError("");
    setStops([...stops, empty()]);
  };
  const updateStop = (i, v) => setStops((xs) => xs.map((s, j) => (j === i ? v : s)));
  const removeStop = (i) => { setStopError(""); setStops((xs) => xs.filter((_, j) => j !== i)); };

  const [food, setFood] = useState(emptyFood());
  const [requested, setRequested] = useState(null);
  const { status: gps, sendNow } = useCustomerLocation();
  const locReady = gps === "on";
  const book = async () => {
    setBooking(true);
    if (!(await sendNow())) { toast.error(LOCATION_REQUIRED_MSG); setBooking(false); return; }
    try {
      const { data } = await api.post("/trips", { pickup: pickup.point, stops: stops.map((s) => s.point), promo_code: promoCode || null, vehicle_class: vehicleClass, origin_url: window.location.origin, ...foodPayload(food), requested_driver_code: requested?.driver_code || null, payment_method: method });
      afterBooking(data);
      if (!data.checkout_url) setBooking(false);
    } catch (e) {
      toast.error(errMsg(e));
      setBooking(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr] lg:gap-8">
      <div className="order-2 min-w-0 space-y-6 lg:order-1">
        <div>
          <p className="lux-eyebrow">New journey</p>
          <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Where to, {user.name.split(" ")[0]}?</h1>
        </div>
        <LocationRequiredBanner />
        <PaymentGate />
        <AdSlot placement="booking" />
        <VehicleCard label="Vehicle class" testId="booking-vehicle-card" />
        <div className="lux-card space-y-5 p-5 sm:p-6" data-testid="booking-form">
          <AddressInput label="Pickup" testId="pickup-input" placeholder="Pickup address" value={pickup} onChange={setPickup} locate />
          {stops.map((s, i) => (
            <AddressInput key={i} label={i === stops.length - 1 ? `Stop ${i + 1} · Final destination` : `Stop ${i + 1}`} testId={`stop-input-${i}`}
              placeholder="Stop address" value={s} onChange={(v) => updateStop(i, v)} onRemove={stops.length > 1 ? () => removeStop(i) : null} />
          ))}
          <div>
            <button type="button" data-testid="add-stop-button" onClick={addStop} className="inline-flex items-center gap-2 text-sm font-medium text-amber-300 transition-colors hover:text-amber-200">
              <Plus className="h-4 w-4" /> Add stop <span className="text-zinc-500">({stops.length}/{MAX_STOPS})</span>
            </button>
            {stopError && <p data-testid="max-stops-error" className="mt-2 text-xs font-medium text-amber-500">{stopError}</p>}
          </div>
        </div>
        <form onSubmit={applyPromo} className="lux-card space-y-2 p-5 sm:p-6" data-testid="promo-form">
          <label className="lux-eyebrow block">Promo code</label>
          <div className="flex gap-2">
            <input data-testid="promo-code-input" className="lux-input min-w-0 flex-1 uppercase" placeholder={promoCode ? "Enter a code to replace" : "e.g. LUXE10"} value={promoInput} onChange={(e) => setPromoInput(e.target.value)} />
            <button type="submit" data-testid="promo-apply-button" disabled={!promoInput.trim()} className="btn-outline-gold !px-5">Apply</button>
          </div>
          {promoCode && (
            <p data-testid="promo-applied" className="flex items-center justify-between text-xs text-emerald-300">
              {promoCode} applied (one promo per trip)
              <button type="button" data-testid="promo-remove-button" onClick={() => setPromoCode("")} className="text-zinc-400 hover:text-red-300">Remove</button>
            </p>
          )}
          {promoError && <p data-testid="promo-error" className="text-xs font-medium text-amber-500">{promoError}</p>}
        </form>
        <RequestDriver value={requested} onChange={setRequested} />
        <FoodRequest value={food} onChange={setFood} />
        {quoting && <p data-testid="fare-loading" className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Calculating route and fare…</p>}
        {quote && !quoting && (
          <div className="space-y-4 fade-up">
            <div className="grid grid-cols-3 gap-2" data-testid="vehicle-class-options">
              {(quote.options || []).map((o) => (
                <button key={o.vehicle_class} type="button" data-testid={`vehicle-class-option-${o.vehicle_class}`} onClick={() => setVehicleClass(o.vehicle_class)}
                  className={`overflow-hidden rounded-xl border text-left transition-colors duration-300 ${vehicleClass === o.vehicle_class ? "border-amber-400 bg-amber-400/10" : "border-white/10 hover:border-amber-500/40"}`}>
                  <img src="/armada-34.jpg" alt="White Nissan Armada Platinum" className="h-14 w-full object-cover" />
                  <span className="block px-2.5 py-2">
                    <span className="block text-xs font-semibold text-amber-100">{CLASS_LABEL[o.vehicle_class]}</span>
                    <span data-testid={`vehicle-class-total-${o.vehicle_class}`} className="block font-serif text-base text-amber-50">{money(o.total)}</span>
                  </span>
                </button>
              ))}
            </div>
            <div className="lux-card p-5 sm:p-6">
              <FareBreakdown fare={fare} miles={quote.trip_miles} minutes={quote.trip_minutes} />
            </div>
          </div>
        )}
        <PaymentMethodPicker value={method} onChange={setMethod} />
        <div>
          <button data-testid="confirm-booking-button" onClick={book} disabled={!user.payment_verified || !locReady || !quote || quoting || booking || foodBlocked(food, user)} className="btn-gold w-full !py-4 text-base">
            {booking && <Loader2 className="h-4 w-4 animate-spin" />}
            {quote ? `Confirm ${CLASS_LABEL[vehicleClass]} & ${payLabel(method, fare.total)}` : "Confirm & pay"}
          </button>
          {!user.payment_verified && <p data-testid="booking-disabled-message" className="mt-2 text-center text-xs text-amber-500">{user.card_message}</p>}
          {user.payment_verified && !locReady && <p data-testid="booking-location-message" className="mt-2 text-center text-xs text-amber-500">{LOCATION_REQUIRED_MSG}</p>}
        </div>
      </div>
      <div className="order-1 min-w-0 lg:order-2 lg:sticky lg:top-28 lg:self-start">
        <MapView className="h-[320px] sm:h-[420px] lg:h-[calc(100vh-10rem)]" pickup={pickup.point} stops={stops.map((s) => s.point)} route={quote?.route_geometry || []} testId="booking-map" />
      </div>
    </div>
  );
};
